'use client';

import { Loader } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { cn } from '@/app/utils';
import { useAudioRecorder } from '../_hooks/use-audio-recorder';
import { MAX_AUDIO_FILE_SIZE, MAX_RECORDING_DURATION, MIN_RECORDING_DURATION } from '../constants';
import type { AudioRecordingFile } from '../types';
import { formatRecordingTime } from '../utils';

interface AudioRecorderProps {
  onRecordingComplete: (file: AudioRecordingFile) => void;
  onMicrophonePermissionGranted?: () => Promise<boolean>;
  consentHint: string;
  /** When true, starting a new recording is disabled until the user removes the existing take (parent clears audio). */
  blockNewRecording?: boolean;
}

const validationDurationValues = {
  minSeconds: MIN_RECORDING_DURATION,
  maxSeconds: MAX_RECORDING_DURATION,
} as const;

const validationFileSizeValues = {
  maxMb: MAX_AUDIO_FILE_SIZE / (1024 * 1024),
} as const;

function discardRecordingFile(file: AudioRecordingFile) {
  URL.revokeObjectURL(file.url);
}

export function AudioRecorder({
  onRecordingComplete,
  onMicrophonePermissionGranted,
  consentHint,
  blockNewRecording = false,
}: AudioRecorderProps) {
  const t = useTranslations();
  const { status, duration, startRecording, stopRecording, reset } = useAudioRecorder();

  const handleStart = useCallback(async () => {
    const result = await startRecording(onMicrophonePermissionGranted);
    if (result.failureReason === 'permission-denied') {
      toast({
        title: t('tools.voiceClone.recorder.permissionDenied'),
      });
    }
  }, [onMicrophonePermissionGranted, startRecording, t]);

  const handleStop = useCallback(async () => {
    const file = await stopRecording();
    if (!file) return;

    if (file.duration < MIN_RECORDING_DURATION) {
      toast({
        title: t('tools.voiceClone.validation.durationTooShort', validationDurationValues),
      });
      discardRecordingFile(file);
      reset();
      return;
    }

    if (file.duration > MAX_RECORDING_DURATION) {
      toast({
        title: t('tools.voiceClone.validation.durationTooLong', validationDurationValues),
      });
      discardRecordingFile(file);
      reset();
      return;
    }

    if (file.size > MAX_AUDIO_FILE_SIZE) {
      toast({
        title: t('tools.voiceClone.validation.fileTooLarge', validationFileSizeValues),
      });
      discardRecordingFile(file);
      reset();
      return;
    }

    onRecordingComplete(file);
  }, [stopRecording, reset, onRecordingComplete, t]);

  useEffect(() => {
    if (duration >= MAX_RECORDING_DURATION && status === 'recording') {
      handleStop();
    }
  }, [duration, status, handleStop]);

  const isRecording = status === 'recording';
  const isStartBlocked = blockNewRecording && !isRecording;
  const deleteToRecordNewHint = t('tools.voiceClone.recorder.deleteToRecordNew');

  return (
    <div className="flex h-full flex-col items-center gap-2">
      <button
        type="button"
        onClick={isRecording ? handleStop : handleStart}
        disabled={status === 'requesting' || isStartBlocked}
        title={isStartBlocked ? deleteToRecordNewHint : undefined}
        className={cn(
          'flex h-[50px] w-[148px] items-center justify-center rounded-[100px] bg-[#6d56f5] text-system-white transition-all duration-300 ease-out enabled:hover:bg-[#7964ff] disabled:opacity-50',
          isRecording && 'flex-col',
        )}
      >
        {status === 'requesting' ? (
          <Loader className="animate-spin text-2xl" />
        ) : isRecording ? (
          <>
            <span className="font-semibold text-[18px]">{formatRecordingTime(duration)}</span>
            <span className="mt-1 flex size-4 items-center justify-center rounded-full border border-system-white">
              <span className="size-2 rounded-full bg-system-white" />
            </span>
          </>
        ) : (
          <MicrophoneIcon className="text-2xl" />
        )}
      </button>
      {!isRecording && status !== 'requesting' && (
        <div className="text-center text-system-white/24 text-xs leading-[1.4]">
          {blockNewRecording ? deleteToRecordNewHint : consentHint}
        </div>
      )}
    </div>
  );
}

function MicrophoneIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 14.5a3.5 3.5 0 0 0 3.5-3.5V7a3.5 3.5 0 1 0-7 0v4a3.5 3.5 0 0 0 3.5 3.5Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
      <path
        d="M5.75 10.5A6.25 6.25 0 0 0 12 16.75m0 0a6.25 6.25 0 0 0 6.25-6.25M12 16.75V20"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
    </svg>
  );
}
